/**
 * Access-token freshness for the PWA. REST calls ride the guest cookie (v2),
 * so the JWT's only live consumer is the Socket.IO handshake — which resolves
 * its credentials on every (re)connection attempt. The token pair is minted
 * once at guest conversion (SubscriptionPage) with a short access TTL
 * (JWT_ACCESS_TTL, 15m default); without refresh the authenticated socket
 * goes permanently dead minutes after conversion.
 */

// Mirrors api/client.ts's API_BASE ('/api/v2' behind the Vite proxy in dev).
// The refresh endpoint lives on the v1 (JWT) surface.
const API_V1_BASE =
  ((import.meta.env.VITE_API_URL as string | undefined) || '/api/v2').replace(
    /\/api\/v2\/?$/,
    '',
  ) + '/api/v1';

// Refresh slightly before actual expiry so a token that passes the check here
// doesn't expire in the milliseconds before the handshake reaches the server.
const REFRESH_SKEW_MS = 60_000;

/**
 * Read `exp` (as epoch ms) from a JWT payload WITHOUT verifying the signature
 * — client-side refresh gating only; the server remains the authority.
 */
export function decodeJwtExpiryMs(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
    ) as { exp?: unknown };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isFresh(token: string): boolean {
  const exp = decodeJwtExpiryMs(token);
  return exp !== null && exp - REFRESH_SKEW_MS > Date.now();
}

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Return a usable access token, refreshing it (single-flight) via
 * `POST /api/v1/auth/refresh` when the stored one is expired or near expiry.
 *
 * - No stored token → null.
 * - Expired with no refresh token → clear the dead JWT (so it can never
 *   shadow a valid guest session on the socket path) and return null.
 * - Refresh rejected (4xx — rotation blocklist, revoked, deleted account) →
 *   both tokens are dead: clear them, return null.
 * - Transient failure (network, 5xx incl. REFRESH_IN_PROGRESS) → keep the
 *   stored pair and return the stale token; the caller's retry loop (socket
 *   reconnection) re-invokes us and the refresh is retried.
 */
export async function ensureFreshAccessToken(): Promise<string | null> {
  const token = localStorage.getItem('access_token');
  if (!token) return null;
  if (isFresh(token)) return token;

  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    localStorage.removeItem('access_token');
    return null;
  }

  refreshInFlight ??= refreshTokens(token, refreshToken).finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function refreshTokens(
  staleToken: string,
  refreshToken: string,
): Promise<string | null> {
  try {
    const res = await fetch(`${API_V1_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) {
      if (res.status >= 400 && res.status < 500) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        return null;
      }
      return staleToken;
    }
    const body = (await res.json()) as {
      data?: { accessToken?: unknown; refreshToken?: unknown };
    };
    const accessToken = body.data?.accessToken;
    if (typeof accessToken !== 'string') return staleToken;
    localStorage.setItem('access_token', accessToken);
    // The server rotates refresh tokens (old one is blocklisted) — persist the
    // replacement or the NEXT refresh fails 401 and logs the user out.
    if (typeof body.data?.refreshToken === 'string') {
      localStorage.setItem('refresh_token', body.data.refreshToken);
    }
    return accessToken;
  } catch {
    return staleToken;
  }
}
